import { useState, type FormEvent } from 'react';
import { APP_NAME, createReportSchema, LEGAL, REPORT_REASONS, type ReportReason } from '@clanfire/shared';
import { reportApi } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { TextArea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useSubmit, validate } from '@/hooks/useForm';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/ui';

const UK = /united kingdom|^uk$|england|wales|scotland|northern ireland/i.test(LEGAL.country);

/** Report a message (its author) or an account to the ClanFire team. */
export function ReportModal({ messageId, userId, subject, onClose }: { messageId?: string; userId?: string; subject: string; onClose: () => void }) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const { loading, error, fields, setFields, run } = useSubmit();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(createReportSchema, { messageId, userId, reason: reason ?? undefined, details });
    if (!data) return setFields(invalid);
    const res = await run(() => reportApi.create(data));
    if (!res) return;
    toast.success(`Thanks for telling us. The ${APP_NAME} team will review it.`);
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Report ${subject}`}
      description={`Reports go to the ${APP_NAME} team, not to the person you're reporting.`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="report-form" variant="danger" loading={loading}>
            Send report
          </Button>
        </>
      }
    >
      <form id="report-form" onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        <fieldset>
          <legend className="mb-2 pl-1 text-[13px] font-semibold text-fg-soft">What's wrong?</legend>
          <div role="radiogroup" className="flex flex-wrap gap-1.5">
            {REPORT_REASONS.map((r, i) => (
              <label
                key={r.id}
                className={cn(
                  'cursor-pointer rounded-full border px-3 py-1.5 text-sm transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent',
                  reason === r.id ? 'border-accent bg-accent-soft font-semibold text-fg' : 'border-line text-fg-soft hover:bg-hover',
                )}
              >
                <input type="radio" name="reason" value={r.id} checked={reason === r.id} onChange={() => setReason(r.id)} className="sr-only" data-autofocus={i === 0 || undefined} />
                {r.label}
              </label>
            ))}
          </div>
          {fields.reason && (
            <p role="alert" className="mt-1.5 pl-1 text-xs font-medium text-danger">
              {fields.reason}
            </p>
          )}
        </fieldset>

        {UK && reason === 'SELF_HARM' && (
          <p className="rounded-2xl bg-accent-2-soft px-4 py-3 text-sm text-accent-2-ink">
            If someone is in danger right now, call <strong>999</strong>. You can talk to Samaritans for free, any time, on <strong>116 123</strong>.
          </p>
        )}
        {UK && reason === 'CHILD_SAFETY' && (
          <p className="rounded-2xl bg-accent-2-soft px-4 py-3 text-sm text-accent-2-ink">
            If a child is in immediate danger, call <strong>999</strong>. You can also report online sexual abuse to the police at{' '}
            <a href="https://www.ceop.police.uk" target="_blank" rel="noopener noreferrer" className="font-semibold underline">
              ceop.police.uk
            </a>
            .
          </p>
        )}

        <TextArea
          label="Anything else we should know? (optional)"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={1000}
          error={fields.details}
          rows={3}
        />
        {(error || fields.messageId) && <p className="text-sm text-danger">{error ?? fields.messageId}</p>}
      </form>
    </Modal>
  );
}
